import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SearchableSelect.module.scss';

/**
 * =============================================================================
 * SEARCHABLE SELECT
 * =============================================================================
 * A `<select>` replacement for the lists in this dialog that are too long to
 * scan: the Pages catalogue, the page sections, and the live programme list.
 *
 * TWO DESIGN DECISIONS WORTH THE WORDS
 *
 * 1. THE LIST OPENS IN FLOW, NOT AS A FLOATING POPOVER.
 *    This lives inside the editor dialog, whose body is the one scrolling
 *    region (`overflow-y: auto`). Per the CSS overflow spec a non-visible
 *    `overflow-y` forces `overflow-x` to compute to `auto` as well, so an
 *    absolutely positioned menu would be CLIPPED by that box the moment the
 *    field sat near its bottom edge — which, in a stepper that shows one short
 *    step at a time, is most of the time. Expanding in flow can never be
 *    clipped: the body simply scrolls, on desktop and on a phone alike.
 *
 * 2. THE SEARCH BOX APPEARS ONLY WHEN IT EARNS ITS PLACE.
 *    Below `searchThreshold` options there is nothing to search — a filter box
 *    over three choices is furniture. The same component therefore serves the
 *    2-option «مصدر الرابط» and the 40-option Pages catalogue without the
 *    caller deciding which gets a search field.
 *
 * Keyboard: Enter/Space/ArrowDown opens, typing filters, ArrowUp/ArrowDown
 * move, Enter picks, Escape closes without changing the value.
 */

const SEARCH_THRESHOLD = 7;

const normalise = (value) =>
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    // Arabic diacritics and tatweel: an operator typing «برنامج» should match
    // «بَرنامج», and neither should be defeated by a stray kashida.
    .replace(/[ً-ْـ]/g, '')
    // alef forms and the ta-marbuta / ha pair, the two commonest near-misses.
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه');

const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'اختر…',
  searchPlaceholder = 'ابحث…',
  emptyLabel = 'لا توجد نتائج مطابقة.',
  disabled = false,
  invalid = false,
  searchThreshold = SEARCH_THRESHOLD,
  ariaLabelledBy,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((option) => option.value === value) ?? null;
  const showSearch = options.length >= searchThreshold;

  const filtered = useMemo(() => {
    const q = normalise(query);
    if (!q) return options;
    return options.filter(
      (option) =>
        normalise(option.label).includes(q) ||
        normalise(option.group).includes(q) ||
        normalise(option.hint).includes(q)
    );
  }, [options, query]);

  /* Close on a click anywhere outside. `mousedown` rather than `click` so the
     list is gone before the click lands on whatever is underneath. */
  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    const index = filtered.findIndex((option) => option.value === value);
    setActiveIndex(index >= 0 ? index : 0);
    // Focus the filter when there is one; otherwise the list keeps the focus
    // the trigger already handed it.
    if (showSearch) requestAnimationFrame(() => searchRef.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!isOpen) return;
    const node = listRef.current?.querySelector('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const commit = (option) => {
    onChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (disabled) return;

    if (!isOpen) {
      if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(filtered.length - 1);
        break;
      case 'Enter':
        event.preventDefault();
        if (filtered[activeIndex]) commit(filtered[activeIndex]);
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={rootRef}
      className={[styles.root, isOpen ? styles.open : '']
        .filter(Boolean)
        .join(' ')}
      onKeyDown={onKeyDown}
    >
      <button
        type='button'
        className={[
          styles.trigger,
          invalid ? styles.invalid : '',
          !selected ? styles.placeholder : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => !disabled && setOpen((open) => !open)}
        disabled={disabled}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-labelledby={ariaLabelledBy}
      >
        <span className={styles['trigger-text']}>
          {selected ? selected.label : placeholder}
          {selected?.hint && (
            <small className={styles['trigger-hint']}>{selected.hint}</small>
          )}
        </span>
        <span className={styles.caret} aria-hidden='true'>
          ▾
        </span>
      </button>

      {isOpen && (
        <div className={styles.panel}>
          {showSearch && (
            <div className={styles['search-row']}>
              <input
                ref={searchRef}
                type='text'
                className={styles.search}
                value={query}
                placeholder={searchPlaceholder}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                }}
                aria-label={searchPlaceholder}
              />
              {query && (
                <button
                  type='button'
                  className={styles['search-clear']}
                  onClick={() => {
                    setQuery('');
                    searchRef.current?.focus();
                  }}
                  aria-label='مسح البحث'
                >
                  <span aria-hidden='true'>&times;</span>
                </button>
              )}
            </div>
          )}

          <ul className={styles.list} role='listbox' ref={listRef}>
            {filtered.length === 0 && (
              <li className={styles.empty}>{emptyLabel}</li>
            )}

            {filtered.map((option, index) => {
              const isSelected = option.value === value;
              // A group heading is repeated only when it changes, so the
              // programme list reads as sections rather than as a label on
              // every row.
              const heading =
                option.group && option.group !== filtered[index - 1]?.group
                  ? option.group
                  : null;

              return (
                <React.Fragment key={option.value || `empty-${index}`}>
                  {heading && (
                    <li className={styles.group} aria-hidden='true'>
                      {heading}
                    </li>
                  )}
                  <li>
                    <button
                      type='button'
                      role='option'
                      aria-selected={isSelected}
                      data-active={index === activeIndex}
                      className={[
                        styles.option,
                        index === activeIndex ? styles['option-active'] : '',
                        isSelected ? styles['option-selected'] : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => commit(option)}
                    >
                      <span className={styles['option-label']}>
                        {option.label}
                        {option.hint && (
                          <small className={styles['option-hint']}>
                            {option.hint}
                          </small>
                        )}
                      </span>
                      {isSelected && (
                        <span className={styles.tick} aria-hidden='true'>
                          ✓
                        </span>
                      )}
                    </button>
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
